import { useContext, useEffect, useState } from 'react';
import {
  FiEdit,
  FiGlobe,
  FiBriefcase,
  FiUsers,
  FiMapPin,
  FiInfo
} from 'react-icons/fi';
import placeholderLogo from '../../../assets/Image Placeholder.jpg';
import { DataProvider } from '../../../store/ContextProvider';
import Loading from '../../../pages/Loading';
import { renderImgSrc } from '../../../utils/renderImgSrc';

export default function ViewProfile({ userData, setIsReadOnly }) {
  
  const [form, setForm] = useState(null);
  const [logoUrl, setLogoUrl] = useState(null);
  const { BeURL } = useContext(DataProvider);

  // Populate form when userData is available
  useEffect(() => {
    if (!userData) return;
    setForm({
      companyName:    userData.companyName || '',
      companyWebsite: userData.companyWebsite || '',
      companyLogo:    userData.companyLogo || '',
      industry:       userData.industry || '',
      companySize:    userData.companySize || '',
      addressLine1:   userData.headquarters?.addressLine1 || '',
      addressLine2:   userData.headquarters?.addressLine2 || '',
      city:           userData.headquarters?.city || '',
      state:          userData.headquarters?.state || '',
      country:        userData.headquarters?.country || '',
      zipCode:        userData.headquarters?.zipCode || '',
      description:    userData.description || ''
    });
  }, [userData]);

  // Generate logo URL when form or BeURL changes
  useEffect(() => {
    if (BeURL && form?.companyLogo) {
      // const tempUrl = `${BeURL}${form.companyLogo}`;
      const tempUrl = renderImgSrc(form.companyLogo, form.companyName)
      setLogoUrl(tempUrl);
    } else {
      setLogoUrl(placeholderLogo);
    }
  }, [BeURL, form?.companyLogo]);

  // Early return only after all hooks
  if (!form || !BeURL || !logoUrl) {
    return <Loading/>;
  }

  const { companyName, companyWebsite, industry, companySize, addressLine1, addressLine2, city, state, country, zipCode, description } = form;

  return (
    <div className="max-w-5xl mx-auto bg-white rounded-lg shadow p-6 space-y-6 mb-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-semibold">Company Profile</h2>
        <button
          onClick={() => setIsReadOnly(false)}
          className="flex items-center gap-1 text-primary-500 hover:text-primary-700"
        >
          <FiEdit /> Edit
        </button>
      </div>

      {/* Logo & Name */}
      <div className="flex flex-col sm:flex-row items-center sm:items-start gap-4">
        <img
          src={logoUrl}
          alt="Company Logo"
          className="w-32 h-32 object-cover rounded border"
        />{console.log("imgString:",logoUrl)}
        <div>
          <h3 className="text-xl font-bold">{companyName}</h3>
          {companyWebsite && (
            <a
              href={companyWebsite}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-1 inline-flex items-center text-primary-600 hover:underline"
            >
              <FiGlobe className="mr-1" /> {companyWebsite.replace(/^https?:\/\//, '')}
            </a>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Industry */}
        <div className="flex items-center gap-2">
          <FiBriefcase className="text-xl text-gray-600" />
          <div>
            <p className="font-medium">Industry</p>
            <p className="text-gray-700">{industry.toUpperCase()}</p>
          </div>
        </div>

        {/* Company Size */}
        <div className="flex items-center gap-2">
          <FiUsers className="text-xl text-gray-600" />
          <div>
            <p className="font-medium">Company Size</p>
            <p className="text-gray-700">{companySize}</p>
          </div>
        </div>

        {/* Headquarters */}
        <div className="flex items-start gap-2">
          <FiMapPin className="text-xl text-gray-600 mt-1" />
          <div>
            <p className="font-medium">Headquarters</p>
            <p className="text-gray-700">
              {addressLine1}
              {addressLine2 ? `, ${addressLine2}` : ''}<br />
              {city}, {state}, {country} {zipCode}
            </p>
          </div>
        </div>

        {/* Description */}
        <div className="flex items-start gap-2 md:col-span-2">
          <FiInfo className="text-xl text-gray-600 mt-1" />
          <div>
            <p className="font-medium">Description</p>
            <p className="text-gray-700 whitespace-pre-wrap">{description}</p>
          </div>
        </div>
      </div>
    </div>
  );
}
